import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { parseJwtPayload } from '../utils/jwtUtils';
import { performCompleteLogout } from '../utils/logoutUtils';
import useUserStore from '../stores/userStore';

/**
 * Unified authentication hook that handles both Auth0 and custom JWT authentication.
 * 
 * This hook provides a consistent authentication interface across the entire app,
 * automatically falling back to localStorage JWT tokens when Auth0 is not available.
 * 
 * Usage:
 * ```typescript
 * const { user, isAuthenticated, isLoading, logout } = useAuth();
 * 
 * if (isLoading) return <Loader />;
 * if (!isAuthenticated) return <LoginPrompt />;
 * 
 * return <div>Hello {user.name}!</div>;
 * ```
 * 
 * Benefits:
 * - Single source of truth for authentication
 * - Handles both Auth0 (OAuth) and custom JWT seamlessly
 * - No need to implement fallback logic in every component
 * - Drop-in replacement for useAuth0()
 * - Type-safe
 */

interface CustomUser {
  sub: string;
  name: string;
  email: string;
  picture?: string;
}

export interface UnifiedAuthUser {
  sub?: string;  // Optional to match Auth0 User type
  name?: string;
  email?: string;
  picture?: string;
  [key: string]: any;  // Allow other Auth0 fields
}

export interface UnifiedAuthReturn {
  user: UnifiedAuthUser | null;
  isAuthenticated: boolean;
  /** True only when the Auth0 SDK considers the user authenticated (not custom-JWT path). */
  auth0IsAuthenticated: boolean;
  isLoading: boolean;
  logout: () => void;
  getAccessTokenSilently?: (options?: any) => Promise<string>;
  loginWithRedirect?: (options?: any) => Promise<void>;
  // Pass through other Auth0 methods
  [key: string]: any;
}

/**
 * Parse JWT token from localStorage and extract user information
 */
const parseCustomUser = (token: string): CustomUser | null => {
  try {
    const payload = parseJwtPayload(token);
    
    if (!payload) {
      console.warn('[useAuth] Invalid JWT structure');
      return null;
    }

    // Validate required fields
    if (!payload.sub) {
      console.warn('[useAuth] Invalid JWT payload - missing sub field');
      return null;
    }

    return {
      sub: payload.sub,
      name: payload.name || 'User',
      email: payload.email || undefined,  // ✅ Use undefined instead of empty string
      picture: payload.picture || undefined  // ✅ Use undefined instead of empty string
    };
  } catch (error) {
    console.error('[useAuth] Error parsing JWT token:', error);
    return null;
  }
};

/**
 * Unified authentication hook
 */
export const useAuth = (): UnifiedAuthReturn => {
  const auth0 = useAuth0();
  const { 
    user: auth0User, 
    isAuthenticated: auth0IsAuthenticated,
    isLoading: auth0IsLoading,
    logout: auth0Logout,
    ...auth0Rest
  } = auth0;

  const [customUser, setCustomUser] = useState<CustomUser | null>(null);
  const [customAuthLoading, setCustomAuthLoading] = useState(true);  // Start as loading

  // Auto-fetch custom user from localStorage when Auth0 user is not available
  useEffect(() => {
    if (!auth0User) {
      setCustomAuthLoading(true);  // Start loading custom auth
      
      // ✅ PREFER id_token (has user info like email), fallback to access_token
      const idToken = localStorage.getItem('id_token');
      const accessToken = localStorage.getItem('authToken');
      const tokenToUse = idToken || accessToken;
      
      if (tokenToUse) {
        // ✅ CHECK EXPIRY FIRST - prevents expired tokens from triggering session monitoring
        const payload = parseJwtPayload(tokenToUse);
        
        if (!payload) {
          // Invalid token structure - clear both
          localStorage.removeItem('authToken');
          localStorage.removeItem('id_token');
          setCustomUser(null);
          setCustomAuthLoading(false);
          return;
        }
        
        // Check if token has expiry and if it's expired
        if (payload.exp) {
          const now = Date.now() / 1000; // JWT exp is in seconds
          if (payload.exp < now) {
            // Token is expired - clear it immediately without triggering auth flows
            localStorage.removeItem('authToken');
            localStorage.removeItem('id_token');
            setCustomUser(null);
            setCustomAuthLoading(false);
            return;
          }
        }
        
        // Token is valid and not expired - proceed with parsing
        const parsed = parseCustomUser(tokenToUse);
        if (parsed) {
          setCustomUser(parsed);
        } else {
          // Invalid token - clear both
          localStorage.removeItem('authToken');
          localStorage.removeItem('id_token');
          setCustomUser(null);
        }
      } else {
        setCustomUser(null);
      }
      setCustomAuthLoading(false);  // Done loading
    } else {
      // Auth0 user is available - clear custom user
      setCustomUser(null);
      setCustomAuthLoading(false);
    }
  }, [auth0User]);
  
  // Listen for storage events to update auth when token changes in another tab or after signup
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken' && !auth0User) {
        const token = e.newValue;
        if (token) {
          // ✅ CHECK EXPIRY FIRST for storage events
          const payload = parseJwtPayload(token);
          
          if (payload && payload.exp) {
            const now = Date.now() / 1000;
            if (payload.exp >= now) {
              // Token is valid and not expired - proceed
          const parsed = parseCustomUser(token);
          if (parsed) {
            setCustomUser(parsed);
              }
            }
            // If expired, ignore the storage event (don't set user)
          }
        } else {
          setCustomUser(null);
        }
      }
    };
    
    // Handle custom auth-refresh event for same-tab updates
    const handleAuthRefresh = () => {
      if (!auth0User) {
        const idToken = localStorage.getItem('id_token');
        const accessToken = localStorage.getItem('authToken');
        const tokenToUse = idToken || accessToken;
        
        if (tokenToUse) {
          // ✅ CHECK EXPIRY FIRST for auth refresh events
          const payload = parseJwtPayload(tokenToUse);
          
          if (payload && payload.exp) {
            const now = Date.now() / 1000;
            if (payload.exp >= now) {
              // Token is valid and not expired - proceed
          const parsed = parseCustomUser(tokenToUse);
          if (parsed) {
            setCustomUser(parsed);
              }
            }
            // If expired, ignore the refresh event (don't set user)
          }
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-refresh', handleAuthRefresh);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-refresh', handleAuthRefresh);
    };
  }, [auth0User]);

  // Unified user (Auth0 takes priority, custom auth is fallback)
  const user = auth0User || customUser;

  // Unified authentication state
  const isAuthenticated = auth0IsAuthenticated || !!customUser;

  // Loading state (Auth0 loading OR custom auth loading)
  const isLoading = auth0IsLoading || customAuthLoading;
  
  // Initialize userStore when user is authenticated (fixes hard refresh issue)
  useEffect(() => {
    if (user?.sub && !isLoading) {
      const currentAuth0Id = useUserStore.getState().auth0Id;
      
      // Only set auth0Id if it's not already set (to avoid redundant API calls)
      if (currentAuth0Id !== user.sub) {
        useUserStore.getState().setAuth0Id(user.sub);
      }
    }
  }, [user?.sub, isLoading]);

  // Unified logout - uses centralized cleanup
  const logout = () => {
    performCompleteLogout({
      auth0Logout: auth0Logout,
      hasAuth0User: !!auth0User,
    });
  };

  return {
    user,
    isAuthenticated,
    auth0IsAuthenticated,
    isLoading,
    logout,
    ...auth0Rest  // Pass through other Auth0 methods
  };
};

export default useAuth;

